import { useState, useCallback, useEffect, useRef } from "react";

type ResizeConstraint = number | ((containerRect: DOMRect) => number);

interface ResizeConstraints {
  maxSize: number;
  minSize: number;
}

interface ResizeDragState {
  constraints: ResizeConstraints;
  containerRect: DOMRect;
}

export interface UseResizableOptions {
  initialSize: number;
  minSize: ResizeConstraint;
  maxSize: ResizeConstraint;
  direction: "horizontal" | "vertical";
  /**
   * Which edge of the container stays put. "start" (default) sizes from the container's left/top
   * edge, for a pane whose handle is on its right/bottom. "end" sizes from the right/bottom edge,
   * for a window anchored there whose handle is on its left/top -- dragging the handle away from
   * the anchored edge grows it.
   */
  anchor?: "start" | "end";
  externalSize?: number; // Allow external control of size
  onSizeChange?: (size: number, constraints: ResizeConstraints) => void; // Callback when size changes
  onSizeCommit?: (size: number, constraints: ResizeConstraints) => void;
  // Use "commit" for heavy panes that should update DOM live but avoid React renders per drag frame.
  stateUpdateMode?: "live" | "commit";
  /** Pixel step for one keyboard resize; Shift multiplies by 4. Defaults to 16. */
  keyboardStep?: number;
}

export interface UseResizableReturn {
  size: number;
  isDragging: boolean;
  /** Attach to onPointerDown for mouse + touch + pen support. */
  handlePointerDown: (e: React.PointerEvent | React.MouseEvent) => void;
  /** @deprecated Same handler as `handlePointerDown`; prefer attaching to onPointerDown. */
  handleMouseDown: (e: React.PointerEvent | React.MouseEvent) => void;
  /** Arrow-key resizing for the drag handle (WAI-ARIA window splitter). Commits immediately. */
  handleKeyDown: (e: React.KeyboardEvent) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export const useResizable = ({
  initialSize,
  minSize,
  maxSize,
  direction,
  anchor = "start",
  externalSize,
  onSizeChange,
  onSizeCommit,
  stateUpdateMode = "live",
  keyboardStep = 16,
}: UseResizableOptions): UseResizableReturn => {
  const [size, setSize] = useState(externalSize ?? initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const onSizeChangeRef = useRef(onSizeChange);
  const onSizeCommitRef = useRef(onSizeCommit);
  const pendingSizeRef = useRef(externalSize ?? initialSize);
  const sizeRef = useRef(externalSize ?? initialSize);
  const dragStateRef = useRef<ResizeDragState | null>(null);

  onSizeChangeRef.current = onSizeChange;
  onSizeCommitRef.current = onSizeCommit;

  useEffect(() => {
    sizeRef.current = size;
    pendingSizeRef.current = size;
  }, [size]);

  useEffect(() => {
    if (externalSize !== undefined && !isDragging) {
      setSize(externalSize);
      pendingSizeRef.current = externalSize;
      sizeRef.current = externalSize;
    }
  }, [externalSize, isDragging]);

  const resolveDragState = useCallback((): ResizeDragState | null => {
    if (!containerRef.current) {
      return null;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const resolveConstraint = (constraint: ResizeConstraint) =>
      typeof constraint === "function" ? constraint(containerRect) : constraint;

    return {
      constraints: {
        maxSize: resolveConstraint(maxSize),
        minSize: resolveConstraint(minSize),
      },
      containerRect,
    };
  }, [maxSize, minSize]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent | React.MouseEvent) => {
      e.preventDefault();
      pendingSizeRef.current = sizeRef.current;
      dragStateRef.current = resolveDragState();
      setIsDragging(true);
    },
    [resolveDragState],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const isVertical = direction === "vertical";
      // Growing moves the handle away from the anchored edge.
      const [growKey, shrinkKey] = isVertical
        ? anchor === "end"
          ? ["ArrowUp", "ArrowDown"]
          : ["ArrowDown", "ArrowUp"]
        : anchor === "end"
          ? ["ArrowLeft", "ArrowRight"]
          : ["ArrowRight", "ArrowLeft"];
      if (e.key !== growKey && e.key !== shrinkKey) {
        return;
      }

      const dragState = resolveDragState();
      if (dragState == null) {
        return;
      }

      e.preventDefault();
      const step = (e.shiftKey ? keyboardStep * 4 : keyboardStep) * (e.key === growKey ? 1 : -1);
      const { constraints } = dragState;
      const newSize = Math.max(
        constraints.minSize,
        Math.min(constraints.maxSize, sizeRef.current + step),
      );
      if (newSize === sizeRef.current) {
        return;
      }

      setSize(newSize);
      pendingSizeRef.current = newSize;
      sizeRef.current = newSize;
      onSizeChangeRef.current?.(newSize, constraints);
      onSizeCommitRef.current?.(newSize, constraints);
    },
    [anchor, direction, keyboardStep, resolveDragState],
  );

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    let animationFrame: number | null = null;
    let latestPointerPosition: { clientX: number; clientY: number } | null = null;

    const applyLatestPointerPosition = () => {
      animationFrame = null;
      const position = latestPointerPosition;
      if (position == null || !containerRef.current) {
        return;
      }

      const dragState = dragStateRef.current ?? resolveDragState();
      if (dragState == null) {
        return;
      }
      dragStateRef.current = dragState;
      const { constraints, containerRect } = dragState;
      const rawSize =
        direction === "horizontal"
          ? anchor === "end"
            ? containerRect.right - position.clientX
            : position.clientX - containerRect.left
          : anchor === "end"
            ? containerRect.bottom - position.clientY
            : position.clientY - containerRect.top;
      const newSize = Math.max(constraints.minSize, Math.min(constraints.maxSize, rawSize));

      pendingSizeRef.current = newSize;
      if (stateUpdateMode === "live") {
        setSize(newSize);
      }
      onSizeChangeRef.current?.(newSize, constraints);
    };

    // Sizing is pointer-position based (not delta based), so replaying only the latest move per
    // animation frame is lossless while capping work at the frame rate.
    const handlePointerMove = (e: PointerEvent | MouseEvent) => {
      latestPointerPosition = { clientX: e.clientX, clientY: e.clientY };
      animationFrame ??= window.requestAnimationFrame(applyLatestPointerPosition);
    };

    // Browsers fire compatibility mouse events after pointer events, so endDrag can be delivered
    // more than once per drag (pointerup then mouseup); it must run its commit exactly once.
    let dragEnded = false;
    const endDrag = () => {
      if (dragEnded) {
        return;
      }
      dragEnded = true;

      if (animationFrame != null) {
        window.cancelAnimationFrame(animationFrame);
        // Apply the final pending move so the commit reflects where the pointer was released.
        animationFrame = null;
        applyLatestPointerPosition();
      }

      const dragState = dragStateRef.current;
      if (stateUpdateMode === "commit") {
        setSize(pendingSizeRef.current);
      }
      if (dragState != null) {
        onSizeCommitRef.current?.(pendingSizeRef.current, dragState.constraints);
      }
      dragStateRef.current = null;
      setIsDragging(false);
    };

    // Pointer events cover mouse, touch, and pen; the handle needs `touch-action: none` so touch
    // drags aren't hijacked by scrolling.
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", endDrag);
    document.addEventListener("pointercancel", endDrag);
    // Mouse fallback for environments without pointer events (jsdom, older embedders). Move
    // handling is rAF-deduped, so double-delivery per movement is harmless.
    document.addEventListener("mousemove", handlePointerMove);
    document.addEventListener("mouseup", endDrag);
    document.body.style.cursor = direction === "horizontal" ? "col-resize" : "row-resize";
    document.body.style.userSelect = "none";

    return () => {
      if (animationFrame != null) {
        window.cancelAnimationFrame(animationFrame);
      }
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", endDrag);
      document.removeEventListener("pointercancel", endDrag);
      document.removeEventListener("mousemove", handlePointerMove);
      document.removeEventListener("mouseup", endDrag);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isDragging, anchor, direction, resolveDragState, stateUpdateMode]);

  return {
    size,
    isDragging,
    handlePointerDown,
    handleMouseDown: handlePointerDown,
    handleKeyDown,
    containerRef,
  };
};
